import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Navigation, useAuth } from '../App';
import { Play, Users, TrendingUp, Star, Calendar, ArrowRight, CheckCircle, BookOpen, MessageSquare, Gamepad2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function HomePage() {
  const { user } = useAuth();
  const [schedule, setSchedule] = useState([]);
  const [panel, setPanel] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [scheduleRes, panelRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/schedule`),
          fetch(`${BACKEND_URL}/api/panel`)
        ]);
        
        if (scheduleRes.ok) setSchedule(await scheduleRes.json());
        if (panelRes.ok) setPanel(await panelRes.json());
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    
    fetchData();
  }, []);

  return (
    <div className="min-h-screen">
      <Navigation />
      
      {/* Hero Section - Poker Table Felt */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" style={{
        background: 'linear-gradient(135deg, #1a4d2e 0%, #2d5f3f 50%, #1a4d2e 100%)',
        position: 'relative'
      }}>
        {/* Felt Texture Overlay */}
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' /%3E%3CfeColorMatrix values='0 0 0 0 0, 0 0 0 0 0, 0 0 0 0 0, 0 0 0 0.05 0'/%3E%3C/filter%3E%3Crect width='100' height='100' filter='url(%23noise)' opacity='0.5'/%3E%3C/svg%3E")`,
          opacity: 0.6
        }}></div>

        {/* Subtle Vignette */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/50"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            {/* Large Logo */}
            <div className="mb-12 flex justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_poker-panel-live/artifacts/u1vz9h4a_Screenshot%202026-03-16%20at%209.57.12%E2%80%AFPM.png"
                alt="Luck or Skills"
                className="w-full max-w-2xl h-auto drop-shadow-2xl"
                style={{ filter: 'drop-shadow(0 10px 30px rgba(0,0,0,0.5))' }}
              />
            </div>

            {/* Tagline */}
            <h2 className="text-2xl sm:text-3xl lg:text-4xl text-white/90 font-light mb-6 tracking-wide" style={{ textShadow: '2px 2px 8px rgba(0,0,0,0.8)' }}>
              Master Texas Hold'em with Real Pros
            </h2>

            <p className="text-lg sm:text-xl text-white/70 max-w-3xl mx-auto mb-12 leading-relaxed">
              Join elite poker sessions, learn winning strategies, and elevate your game<br className="hidden sm:block" />
              with guidance from experienced Texas players.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
              {user ? (
                <Link
                  to="/app/play"
                  className="group bg-[#C9A227] hover:bg-[#E1C25B] text-[#0B0F14] px-12 py-5 rounded-lg text-xl font-bold shadow-2xl hover:shadow-[#C9A227]/50 transition-all transform hover:scale-105 flex items-center gap-3"
                  data-testid="hero-enter-poker-room-button"
                >
                  <Gamepad2 className="w-7 h-7" />
                  Enter Poker Room
                  <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
                </Link>
              ) : (
                <>
                  <Link
                    to="/live"
                    className="group bg-red-600 hover:bg-red-700 text-white px-10 py-4 rounded-lg text-lg font-bold shadow-2xl hover:shadow-red-900/50 transition-all transform hover:scale-105 flex items-center gap-3"
                    data-testid="hero-watch-live-button"
                  >
                    <Play className="w-6 h-6" />
                    Watch Live
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <Link
                    to="/membership"
                    className="group bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white border-2 border-white/30 px-10 py-4 rounded-lg text-lg font-bold shadow-2xl transition-all transform hover:scale-105 flex items-center gap-3"
                    data-testid="hero-join-button"
                  >
                    Explore Membership
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </>
              )}
            </div>

            {/* Trust Indicators */}
            <div className="grid grid-cols-3 gap-8 max-w-3xl mx-auto pt-8 border-t border-white/20">
              <div className="text-center">
                <div className="text-4xl font-bold text-white mb-2">500+</div>
                <div className="text-sm text-white/60 uppercase tracking-wider">Members</div>
              </div>
              <div className="text-center border-x border-white/20">
                <div className="text-4xl font-bold text-white mb-2">50+</div>
                <div className="text-sm text-white/60 uppercase tracking-wider">Live Sessions</div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-white mb-2 flex items-center justify-center gap-1">
                  4.9 <Star className="w-6 h-6 fill-yellow-400 text-yellow-400" />
                </div>
                <div className="text-sm text-white/60 uppercase tracking-wider">Rating</div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white/50 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Features Section - Premium Dark */}
      <section className="py-24 bg-gradient-to-b from-background to-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-heading font-bold mb-6">
              Everything You Need to Win
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Professional training, expert guidance, and a community of winners.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <Play className="w-8 h-8" />,
                title: "Live Sessions",
                description: "Watch expert players break down strategy in real-time discussions.",
                color: "from-red-600/20 to-red-900/20",
                link: "/features/live-sessions"
              },
              {
                icon: <TrendingUp className="w-8 h-8" />,
                title: "Skill Development",
                description: "Track your progress from beginner to advanced player.",
                color: "from-green-600/20 to-green-900/20",
                link: "/features/skill-development"
              },
              {
                icon: <Users className="w-8 h-8" />,
                title: "Elite Community",
                description: "Connect with serious players and get personalized feedback.",
                color: "from-blue-600/20 to-blue-900/20",
                link: "/features/community"
              },
              {
                icon: <Star className="w-8 h-8" />,
                title: "Pro Coaching",
                description: "Learn from Texas natives with decades of winning experience.",
                color: "from-yellow-600/20 to-yellow-900/20",
                link: "/features/coaching"
              }
            ].map((feature, idx) => (
              <Link
                key={idx}
                to={feature.link}
                className="group relative bg-card border border-border rounded-2xl p-8 hover:border-primary/50 transition-all duration-300 overflow-hidden cursor-pointer"
                data-testid={`feature-card-${feature.link.split('/').pop()}`}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mb-6 text-primary group-hover:scale-110 transition-transform duration-300">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Panel Section - Premium */}
      <section className="py-24 bg-card/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-heading font-bold mb-6">
              Your Expert Instructors
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Learn from real Texans who've mastered the game and love teaching it.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {panel.slice(0, 3).map(member => (
              <Link
                to="/about"
                key={member.member_id} 
                className="group bg-gradient-to-b from-card to-card/50 border border-border rounded-2xl p-8 hover:border-primary/50 transition-all duration-300 text-center cursor-pointer"
                data-testid={`panel-member-card-${member.member_id}`}
              >
                <div className="relative inline-block mb-6">
                  <img
                    src={member.picture_url}
                    alt={member.name}
                    className="w-32 h-32 rounded-full border-4 border-primary/20 group-hover:border-primary/50 transition-colors object-cover"
                  />
                  <div className="absolute -bottom-2 -right-2 w-10 h-10 bg-primary rounded-full flex items-center justify-center border-4 border-card">
                    <Star className="w-5 h-5 text-black fill-black" />
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-2">{member.name}</h3>
                <p className="text-sm text-primary font-semibold mb-3">{member.poker_background}</p>
                <p className="text-muted-foreground text-sm leading-relaxed">{member.bio}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Schedule Section */}
      {schedule.length > 0 && (
        <section className="py-24 bg-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-12">
              <div>
                <h2 className="text-4xl font-heading font-bold mb-2">Upcoming Sessions</h2>
                <p className="text-muted-foreground text-lg">Don't miss our live poker discussions</p>
              </div>
              <Link 
                to="/live" 
                className="hidden sm:flex items-center gap-2 text-primary hover:text-primary/80 font-semibold group"
              >
                View All
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {schedule.slice(0, 3).map(item => (
                <Link
                  to="/features/live-sessions"
                  key={item.schedule_id} 
                  className="bg-card border border-border rounded-xl p-6 hover:border-primary/50 hover:shadow-lg hover:shadow-primary/10 transition-all cursor-pointer"
                  data-testid={`schedule-card-${item.schedule_id}`}
                >
                  <div className="flex items-center gap-2 text-primary font-semibold mb-4">
                    <Calendar className="w-5 h-5" />
                    <span className="text-sm">
                      {new Date(item.scheduled_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                  <p className="text-muted-foreground mb-4 leading-relaxed">{item.description}</p>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>{new Date(item.scheduled_at).toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit'
                    })}</span>
                    <span>•</span>
                    <span>{item.duration_minutes} min</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section - Premium */}
      <section className="py-32 bg-gradient-to-br from-primary/10 via-background to-primary/5 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 bg-primary rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-64 h-64 bg-primary rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold mb-6">
            Ready to Level Up?
          </h2>
          <p className="text-xl sm:text-2xl text-muted-foreground mb-12">
            Join our community and start your journey to poker mastery.
          </p>
          
          <Link
            to="/membership"
            className="inline-flex items-center gap-3 bg-primary hover:bg-primary/90 text-black px-12 py-5 rounded-xl text-xl font-bold shadow-2xl hover:shadow-primary/50 transition-all transform hover:scale-105"
          >
            Get Started Now
            <ArrowRight className="w-6 h-6" />
          </Link>

          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span>Cancel Anytime</span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-border"></div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span>Instant Access</span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-border"></div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span>Join 500+ Members</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-12 bg-card/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="font-bold mb-4 text-lg">About</h4>
              <ul className="space-y-3 text-muted-foreground">
                <li><Link to="/about" className="hover:text-foreground transition-colors">Our Story</Link></li>
                <li><Link to="/about" className="hover:text-foreground transition-colors">The Panel</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-lg">Learn</h4>
              <ul className="space-y-3 text-muted-foreground">
                <li><Link to="/lessons" className="hover:text-foreground transition-colors">Lessons</Link></li>
                <li><Link to="/archives" className="hover:text-foreground transition-colors">Archives</Link></li>
                <li><Link to="/practice" className="hover:text-foreground transition-colors">Practice Hands</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-lg">Community</h4>
              <ul className="space-y-3 text-muted-foreground">
                <li><Link to="/community" className="hover:text-foreground transition-colors">Forum</Link></li>
                <li><Link to="/live" className="hover:text-foreground transition-colors">Live Streams</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-lg">Join</h4>
              <ul className="space-y-3 text-muted-foreground">
                <li><Link to="/membership" className="hover:text-foreground transition-colors">Membership</Link></li>
                <li><Link to="/booking" className="hover:text-foreground transition-colors">Book Free Call</Link></li>
                <li><Link to="/login" className="hover:text-foreground transition-colors">Sign In</Link></li>
              </ul>
            </div>
          </div>
          
          {/* Business Info & Compliance */}
          <div className="pt-8 border-t border-border">
            <div className="text-center mb-6">
              <p className="font-bold text-lg mb-2">Luck or Skills</p>
              <p className="text-muted-foreground text-sm">New Braunfels, TX 78130</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-3 text-sm">
                <a href="mailto:info@luckorskills.com" className="text-primary hover:text-primary/80 transition-colors">
                  info@luckorskills.com
                </a>
                <span className="hidden sm:inline text-muted-foreground">•</span>
                <a href="tel:+18302372821" className="text-primary hover:text-primary/80 transition-colors">
                  (830) 237-2821
                </a>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6 text-sm">
              <Link to="/privacy-policy" className="text-primary hover:text-primary/80 transition-colors underline">
                Privacy Policy
              </Link>
              <span className="hidden sm:inline text-muted-foreground">•</span>
              <Link to="/terms-of-service" className="text-primary hover:text-primary/80 transition-colors underline">
                Terms of Service
              </Link>
            </div>

            <div className="text-center">
              <p className="text-muted-foreground mb-2">© 2024 Luck or Skills - Texas Hold'em Learning Community</p>
              <p className="text-sm text-muted-foreground/80">Educational Poker Platform • Not a Gambling Site</p>
              <p className="text-xs text-muted-foreground/40 mt-3 italic">"Kiss a Pig" - Patrick Lynch</p>
              
              {/* Admin Access Link */}
              <div className="mt-4">
                <Link 
                  to="/login?admin=true" 
                  className="text-sm text-[#C9A227] hover:text-yellow-300 transition-colors underline font-medium"
                  data-testid="footer-admin-link"
                >
                  🔐 Admin Access
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
